import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import type { MemberRecord, WorkspaceCreditSummary } from '../types'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string) {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

// Carries the backend AppError's structured `details` payload (e.g.
// `{ unallocated: number }` on `allocation_exceeds_pool`) alongside the
// human-readable message, so callers can surface specifics when useful.
export class ApiError extends Error {
  code?: string
  details?: unknown
  constructor(message: string, code?: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.details = details
  }
}

async function apiFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(buildUrl(path), {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new ApiError('Session expired')
    }
    const p = await res.json().catch(() => null)
    // Zod field-validation errors shape `details` as an array of {field, message} —
    // fold those into the message so callers who just read `.message` still get
    // something readable. Other error codes (e.g. allocation_exceeds_pool) shape
    // `details` as a plain object instead — leave those on `.details` as-is for
    // callers that specifically read them.
    const details = Array.isArray(p?.error?.details) ? p.error.details : null
    const detailMessage = details?.length
      ? details.map((d: { field?: string; message?: string }) => d.message || d.field).join('; ')
      : null
    const message = detailMessage || p?.error?.message || p?.message || res.statusText || 'Request failed'
    throw new ApiError(message, p?.error?.code, p?.error?.details)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

export function listMembers(workspaceId: string): Promise<MemberRecord[]> {
  return apiFetch('GET', `/api/workspaces/${workspaceId}/members`)
}

export function updateMemberRole(membershipId: string, role: 'admin' | 'member'): Promise<MemberRecord> {
  return apiFetch('PATCH', `/api/memberships/${membershipId}`, { role })
}

export function removeMember(membershipId: string): Promise<void> {
  return apiFetch('DELETE', `/api/memberships/${membershipId}`)
}

export function getWorkspaceCredits(workspaceId: string): Promise<WorkspaceCreditSummary> {
  return apiFetch('GET', `/api/workspaces/${workspaceId}/credits`)
}

export function updateMemberAllocation(
  membershipId: string,
  aiCreditAllocation: number | null,
): Promise<MemberRecord> {
  return apiFetch('PATCH', `/api/memberships/${membershipId}/credits`, { aiCreditAllocation })
}
